/**
 * 文字のグラデーション (design.gradient) の正本。テレビのバラエティ番組風テロップの
 * 「上が白く下が黄色」「上が白く下が黒っぽい (ネガティブ)」のような 2 色の塗り。
 *
 * 同じ意味・同じ規則の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI)
 * - video-editor リポジトリの src/schema/captionGradient.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/caption_extensions.py (AI 提案の正規化・要約)
 *
 * ## 形
 *
 * `CaptionDesign.gradient = { color, direction?, split? }`
 * - 1 色目は文字色 (`design.color`) そのもの。**gradient は 2 色目と向きだけを持つ**
 *   (文字色を変えれば 1 色目が追従し、グラデーションを知らない書き出し (Premiere / Final Cut の
 *   テキスト) は文字色の単色で出る)
 * - `color`: 2 色目 (#RRGGBB)。向きが vertical なら下、horizontal なら右の色
 * - `direction`: 'vertical' (上 → 下。既定・保存時はキーを落とす) / 'horizontal' (左 → 右)
 * - `split`: true で中央でくっきり 2 色に分ける (ツートン)。省略 / false = なだらか
 *
 * 2 色目が #RRGGBB でなければ無効 (キーごと落とす = 保存値は実効値)。中抜き (hollow) では
 * 輪郭線に塗りが無いので描画は無視するが、値は保持する (中抜きを外せば戻る)。
 *
 * ## 描画
 *
 * 文字本体の層とは**別に、最前面にもう 1 層**同じレイアウトの文字を重ね、その層にだけ
 * `background-image: linear-gradient(...)` + `background-clip: text` + 透明の文字色を当てる。
 * 文字本体の層は文字を透明にして縁取り (丸角の text-shadow リング) と箱の背景だけを担う。
 * 同じ要素に text-shadow と background-clip: text を置くと影が塗りの上に描かれるため、層を分ける。
 *
 * グラデーションは**行ごと** (横書きは行ボックス 1 つぶん、縦書きは列 1 本ぶん) に掛ける。
 * 色の変わる区間は行 (列) の 20%〜80% (CAPTION_GRADIENT_SMOOTH_START / END。字形は行ボックスの
 * 中央に寄るので、字の上端は 1 色目、下端は 2 色目に近づく)。split は 50% でくっきり分ける。
 *
 * ## 書き出し
 *
 * Premiere (FCP7 XML) / Final Cut のテキストにはグラデーション塗りが無いので落とし、文字色の
 * 単色で出す (§0-2)。
 */

export const CAPTION_GRADIENT_DIRECTIONS = ['vertical', 'horizontal'] as const;
export type CaptionGradientDirection = (typeof CAPTION_GRADIENT_DIRECTIONS)[number];
export const CAPTION_GRADIENT_DEFAULT_DIRECTION: CaptionGradientDirection = 'vertical';

/** なだらかなグラデーションで色が変わる区間 (行 / 列の長さに対する割合)。 */
export const CAPTION_GRADIENT_SMOOTH_START = 0.2;
export const CAPTION_GRADIENT_SMOOTH_END = 0.8;
/** split (ツートン) の境界 (行 / 列の長さに対する割合)。 */
export const CAPTION_GRADIENT_SPLIT_AT = 0.5;

/** UI でグラデーションを入れたときの 2 色目の初期値 (文字色が同じ黄色なら白)。 */
export const CAPTION_GRADIENT_DEFAULT_SECOND_COLOR = '#FFD400';

export interface CaptionGradient {
  color: string;
  direction?: CaptionGradientDirection;
  split?: boolean;
}

/** 実効値 (描画側はこれだけを見る)。 */
export interface ResolvedCaptionGradient {
  color: string;
  direction: CaptionGradientDirection;
  split: boolean;
}

const HEX6 = /^#[0-9a-fA-F]{6}$/;

/**
 * 保存形へ正規化する。2 色目が #RRGGBB でなければ undefined (キーごと落とす)。
 * 既定の向き (vertical) と split: false はキーを落とす (保存値 = 実効値)。
 */
export function normalizeCaptionGradient(value: unknown): CaptionGradient | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Partial<CaptionGradient>;
  if (typeof raw.color !== 'string' || !HEX6.test(raw.color)) return undefined;
  const result: CaptionGradient = { color: raw.color.toUpperCase() };
  if (
    typeof raw.direction === 'string'
    && (CAPTION_GRADIENT_DIRECTIONS as readonly string[]).includes(raw.direction)
    && raw.direction !== CAPTION_GRADIENT_DEFAULT_DIRECTION
  ) {
    result.direction = raw.direction;
  }
  if (raw.split === true) result.split = true;
  return result;
}

/**
 * design の実効値。中抜き (hollow) では輪郭線に塗りが無いので undefined (描画しない)。
 * グラデーションが無い / 無効なら undefined。
 */
export function resolveCaptionGradient(
  design: { gradient?: CaptionGradient | null; hollow?: boolean } | null | undefined,
): ResolvedCaptionGradient | undefined {
  if (!design || design.hollow) return undefined;
  const gradient = normalizeCaptionGradient(design.gradient);
  if (!gradient) return undefined;
  return {
    color: gradient.color,
    direction: gradient.direction ?? CAPTION_GRADIENT_DEFAULT_DIRECTION,
    split: gradient.split === true,
  };
}

/** グラデーションが有効な design か (要約・UI で使う。中抜きでは false)。 */
export function hasCaptionGradient(
  design: { gradient?: CaptionGradient | null; hollow?: boolean } | null | undefined,
): boolean {
  return resolveCaptionGradient(design) !== undefined;
}

/**
 * CSS の linear-gradient() 文字列。`baseColor` (文字色) から gradient.color へ。
 * 向きは vertical = to bottom、horizontal = to right。
 */
export function captionGradientImage(baseColor: string, gradient: ResolvedCaptionGradient): string {
  const to = gradient.direction === 'horizontal' ? 'to right' : 'to bottom';
  const pct = (ratio: number) => `${Math.round(ratio * 1000) / 10}%`;
  if (gradient.split) {
    return `linear-gradient(${to}, ${baseColor} ${pct(CAPTION_GRADIENT_SPLIT_AT)}, ${gradient.color} ${pct(CAPTION_GRADIENT_SPLIT_AT)})`;
  }
  return `linear-gradient(${to}, ${baseColor} ${pct(CAPTION_GRADIENT_SMOOTH_START)}, ${gradient.color} ${pct(CAPTION_GRADIENT_SMOOTH_END)})`;
}

/**
 * 文字にグラデーションを塗る層の CSS。
 * @param lineBoxPx 横書きのブロック (inline-block) に掛けるときの行送り (px)。背景をこの高さで
 *   繰り返して**行ごと**にグラデーションを掛ける。inline (箱) / 縦書きの列は要素 1 つが 1 行 (列) なので省略
 */
export function captionGradientTextStyle(
  baseColor: string,
  gradient: ResolvedCaptionGradient,
  lineBoxPx?: number,
): {
  backgroundImage: string;
  backgroundSize?: string;
  backgroundRepeat?: string;
  WebkitBackgroundClip: 'text';
  backgroundClip: 'text';
  color: 'transparent';
  WebkitTextFillColor: 'transparent';
} {
  return {
    backgroundImage: captionGradientImage(baseColor, gradient),
    ...(lineBoxPx !== undefined ? { backgroundSize: `100% ${lineBoxPx}px`, backgroundRepeat: 'repeat' } : {}),
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  };
}
